import MultipleSelect, {
  Props as MultipleSelectProps,
} from "@/components/atoms/PortalMultipleSelect";
import {
  Select as SelectComponent,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue,
} from "@/components/atoms/Select";
import clsx from "clsx";
import { useState, useRef, useEffect } from "react";
import Input from "@/components/atoms/Input";
import ReactDOM from "react-dom";

type Props =
  | {
      multiple: true;
      value: (string | number)[];
      disabled?: boolean;
      options: { value: string; label: string, disabled?: boolean }[];
      placeholder?: string;
      children?: React.ReactNode;
      className?: string;
      onChange: (value: string[]) => void;
      enabledSearch?: boolean;
      enableTags?: boolean;
    }
  | {
      multiple?: false;
      value: string;
      disabled?: boolean;
      options: { value: string; label: string, disabled?: boolean }[];
      placeholder?: string;
      children?: React.ReactNode;
      className?: string;
      onChange: (value: string) => void;
      enabledSearch?: boolean;
    };

const PortalSelect = ({
  multiple,
  value,
  disabled,
  options,
  placeholder,
  children,
  className,
  onChange,
  enabledSearch=false,
}: Props) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const portalRoot = document.body;
  useEffect(() => {
    if (isOpen && enabledSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, enabledSearch]);

  const handleTriggerClick = () => {
    setIsOpen((prev) => !prev);
  };

  if (multiple) {
    const props = {
      value,
      disabled,
      options,
      placeholder,
      children,
      className,
      onChange,
      enabledSearch,
    };
    return <MultipleSelect {...props} />;
  } else
    return (
      <SelectComponent
        value={value || ""}
        onValueChange={(val) => {
          onChange(val);
          setIsOpen(false);
        }}
        disabled={disabled}
        onOpenChange={(open) => setIsOpen(open)}
      >
        <SelectTrigger onClick={handleTriggerClick} className={clsx("w-full", className)}>
          <SelectValue placeholder={placeholder || "Select"} />
        </SelectTrigger>
        {isOpen &&
          ReactDOM.createPortal(
            <SelectContent
              className="absolute overflow-y-auto bg-white shadow-lg z-50"
              style={{ top: "100%", left: 0, right: 0 }}
              onCloseAutoFocus={() => setIsOpen(false)}
              onKeyDown={(e) => {
                if (enabledSearch && searchInputRef.current) {
                  searchInputRef.current.focus();
                  e.stopPropagation();
                }
              }}
            >
              {enabledSearch && (
                <Input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full p-2 border-b border-gray-300 sticky top-0 z-10 bg-white"
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                />
              )}
              {options
                .filter((option) => option.label.toLowerCase().includes(search))
                .map(({ label, value, disabled }, index) => (
                  <SelectItem key={`${value}-${index}`} value={value} disabled={disabled}>
                    {children || label}
                  </SelectItem>
                ))}
            </SelectContent>,
            portalRoot
          )}
      </SelectComponent>
    );
};

export default PortalSelect;
